import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { SendMessagePayload } from '@klaro7/shared-types';

import {
  getConversations,
  getMessages,
  sendMessage,
  markAsRead,
  markAsUnread,
  getUnansweredMessageCount,
} from '../services/chat.service';

export function useConversations(enabled = true) {
  return useQuery({
    queryKey: ['chat-conversations'],
    queryFn: getConversations,
    staleTime: 1000 * 30,
    enabled,
  });
}

export function useMessages(conversationId: string | undefined) {
  return useQuery({
    queryKey: ['chat-messages', conversationId],
    queryFn: () => getMessages(conversationId!, { limit: 100 }),
    enabled: !!conversationId,
    staleTime: 1000 * 10,
  });
}

export function useSendMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SendMessagePayload) => sendMessage(data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['chat-conversations'] });
      queryClient.invalidateQueries({ queryKey: ['chat-daily-count'] });
      if (variables.conversationId) {
        queryClient.invalidateQueries({
          queryKey: ['chat-messages', variables.conversationId],
        });
      }
    },
  });
}

export function useMarkAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (conversationId: string) => markAsRead(conversationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-conversations'] });
    },
  });
}

export function useMarkAsUnread() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (conversationId: string) => markAsUnread(conversationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-conversations'] });
    },
  });
}

/** Consecutive un-replied messages in the active conversation (limit resets on reply). */
export function useUnansweredMessageCount(conversationId?: string) {
  return useQuery({
    queryKey: ['chat-daily-count', conversationId],
    queryFn: () => getUnansweredMessageCount(conversationId),
    staleTime: 1000 * 30,
  });
}
